import {FormField} from '@service/scripts/components/contentful/contactform_v2/FormField.jsx';
import {Alert, Button} from '@bonprix/design-system';
import React, {useEffect, useReducer, useRef, useState} from 'react';
import {TrinityEvents} from '@service/scripts/trinity.js';
import fetchWithCsrf from '@core/scripts/helper/fetch-with-csrf.js';
import Url from '@core/scripts/helper/url.js';
import {WidgetInstance} from 'friendly-challenge';
import RichText from '@service/scripts/components/contentful/RichText.jsx';
import {Icon} from 'bonprix-design-system';

function formReducer(state, action) {
    return {...state, ...action};
}

function getParameterFromHash(name) {
    const hash = location.hash.substring(1);
    const params = new URLSearchParams(hash);
    return params.get(name);
}

function getInitialState(fields, customerData) {
    // Map customer data fields to form field names
    const customerFieldMapping = {
        firstname: customerData?.firstName ?? '',
        lastname: customerData?.lastName ?? '',
        email: customerData?.email ?? '',
        emailCompare: customerData?.email ?? '',
        customerId: customerData?.customerId ?? '',
    };

    const parameters = fields
        .filter(field => field.fieldType !== 'FILE_UPLOAD')
        .reduce((acc, field) => {
            // URL hash params take priority over customer data
            const hashValue = getParameterFromHash(field.name);
            if (hashValue) {
                acc[field.name] = hashValue;
            } else if (customerFieldMapping[field.name] !== undefined) {
                // Use customer data if available for this field
                acc[field.name] = customerFieldMapping[field.name];
            } else {
                acc[field.name] = '';
            }
            return acc;
        }, {});

    const newUrl = window.location.href.split('#')[0];
    window.history.replaceState({}, document.title, newUrl);
    return parameters;
}

// Utility to trim all string values in an object
function trimFormValues(values) {
    const trimmed = {};
    for (const key in values) {
        if (typeof values[key] === 'string') {
            trimmed[key] = values[key].trim();
        } else {
            trimmed[key] = values[key];
        }
    }
    return trimmed;
}

// Utility to check if a string contains only letters, spaces, hyphens, apostrophes
function isAlpha(value) {
    return /^[\p{L} '-]+$/u.test(value);
}

// Validate form values based on Contentful field config
function validateFormValues(fields, values) {
    const errors = {};
    fields.forEach((field) => {
        const value = values[field.name] || '';
        // Min length check
        if (field.minLength && value.length > 0 && value.length < field.minLength) {
            errors[field.name] = true;
        }
        if (field.maxLength && value.length > 0 && value.length > field.maxLength) {
            errors[field.name] = true;
        }
        // Alpha check
        if (field.alpha && value && !isAlpha(value)) {
            errors[field.name] = true;
        }
    });
    // Email compare check (case-insensitive)
    if ('email' in values && 'emailCompare' in values) {
        const email = (values.email || '').trim().toLowerCase();
        const emailCompare = (values.emailCompare || '').trim().toLowerCase();
        if (email !== emailCompare) {
            errors.emailCompare = true;
        }
    }
    return errors;
}

export function ContactFormV2({
    cdaEntry,
    customerData,
    friendlyCaptchaEnabled,
    friendlyCaptchaSitekeyNewsletter,
    friendlyCaptchaSitekeyNewsletterAt,
    tenantCountry,
}) {
    const [formValues, setFormValues] = useReducer(formReducer, getInitialState(cdaEntry.fields ?? [], customerData));
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');
    const [solution, setSolution] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);
    const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(friendlyCaptchaEnabled);
    const formRef = useRef();
    const fcContainer = useRef();
    const fcWidget = useRef();
    const friendlyCaptchaSitekey =
        tenantCountry === 'at' ? friendlyCaptchaSitekeyNewsletterAt : friendlyCaptchaSitekeyNewsletter;

    useEffect(() => {
        if (!friendlyCaptchaEnabled) {
            return;
        }

        if (!fcWidget.current && fcContainer.current) {
            fcWidget.current = new WidgetInstance(fcContainer.current, {
                doneCallback: (solution) => {
                    setSolution(solution);
                    setSubmitButtonIsDisabled(false);
                },
            });
        }

        return () => {
            if (fcWidget.current !== undefined) fcWidget.current.reset();
        };
    }, [fcContainer]);

    useEffect(() => {
        setFormValues({...formValues, 'frc-captcha-solution': solution});
    }, [solution]);

    const handleChange = (event, fieldName, index) => {
        // If event, get value; if value, use directly
        const value = event?.target ? event.target.value : event;
        if (typeof index === 'number') {
            const newValues = [...(formValues[fieldName] || [])];
            newValues[index] = value;

            setFormValues({
                ...formValues,
                [fieldName]: newValues,
            });
        } else {
            setFormValues({...formValues, [fieldName]: value});
        }
        // Clear subject error if subject is chosen
        if (fieldName === 'subject' && value) {
            setFormErrors((prevErrors) => {
                const {subject, ...rest} = prevErrors;
                return rest;
            });
        }
        if (fieldName === 'concern' && value) {
            setFormErrors((prevErrors) => {
                const {concern, ...rest} = prevErrors;
                return rest;
            });
        }
    };

    const handleFilesChange = (files) => {
        setUploadedFiles(files);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setFormErrors({});
        setErrorMsg('');
        // Validate subject selection before submitting
        if (!formValues.subject) {
            setFormErrors({subject: true});
            setErrorMsg('');
            // Optionally scroll to subject field
            const subjectField = formRef.current.querySelector('[name="subject"]');
            subjectField?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            subjectField?.focus();
            setIsSubmitting(false);
            return;
        }
        // Frontend validations
        const validationErrors = validateFormValues(cdaEntry.fields, formValues);
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(true);
        TrinityEvents.sendClickContact('sendContactForm', 'button');
        const trimmedFormValues = trimFormValues(formValues);

        // Convert files to Base64 for JSON submission
        const convertFilesToBase64 = async (files) => {
            if (!files || files.length === 0) {
                return [];
            }

            const filePromises = files.map(file =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const result = reader.result;
                        if (typeof result !== 'string') {
                            reject(new Error(`Failed to read file: ${file.name}`));
                            return;
                        }
                        const commaIndex = result.indexOf(',');
                        if (commaIndex < 0) {
                            reject(new Error(`Failed to read file: ${file.name}`));
                            return;
                        }
                        const base64 = result.slice(commaIndex + 1); // Strip data:*/*;base64, prefix
                        resolve({
                            filename: file.name,
                            mimeType: file.type,
                            content: base64
                        });
                    };
                    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
                    reader.readAsDataURL(file);
                })
            );

            return Promise.all(filePromises);
        };

        convertFilesToBase64(uploadedFiles)
            .then(filesData => {
                const payload = {
                    formId: cdaEntry.id,
                    fields: trimmedFormValues,
                    files: filesData
                };

                const submitUrl = Url.toAbsolute(cdaEntry.submitUrl);

                return fetchWithCsrf(submitUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
            })
            .then((response) => response.json())
            .then((data) => {
                if (data.errors) {
                    setFormErrors(data.errors);
                    //try to scroll to first error
                    const firstErrorField = formRef.current.querySelector(`[name="${Object.keys(data.errors)[0]}"]`);
                    firstErrorField?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
                    firstErrorField?.focus();
                }
                if (data.errorMsg) {
                    setErrorMsg(cdaEntry.genericErrorMessage);
                }

                if (data.errors || data.errorMsg) {
                    if (friendlyCaptchaEnabled) {
                        fcWidget.current.reset();
                    }
                }

                //redirect to success page
                if (data.success) {
                    window.location = Url.toAbsolute(cdaEntry.successPageUrl);
                }
            })
            .catch(() => {
                setErrorMsg(cdaEntry.genericErrorMessage);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="service-page-contact-form">
            {cdaEntry?.notes?.visible ? (
                <div className="preflight m-0 flex justify-between items-start p-5 rounded gap-4 bg-clr-bg-alert">
                    <div className="flex gap-2">
                        <Icon variant="hint" className="fill-clr-fg-alert" />
                        <RichText richTextModel={cdaEntry?.notes?.text} />
                    </div>
                </div>
            ) : null}
            <h2 className="typography-h2">{cdaEntry.headline}</h2>
            <form onSubmit={handleSubmit} ref={formRef}>
                {cdaEntry.fields &&
                    cdaEntry.fields.map((formField, index) => (
                        <FormField
                            formField={formField}
                            formValues={formValues}
                            formErrors={formErrors}
                            key={`${formField.name}_${index}`}
                            tenantCountry={tenantCountry}
                            handleChange={handleChange}
                            setIsSubmitEnabled={setIsSubmitEnabled}
                            fcContainer={fcContainer}
                            friendlyCaptchaSitekey={friendlyCaptchaSitekey}
                            friendlyCaptchaEnabled={friendlyCaptchaEnabled}
                            setFormValues={setFormValues}
                            uploadedFiles={uploadedFiles}
                            onFilesChange={handleFilesChange}
                        />
                    ))}
                {errorMsg && (
                    <div className="form-row">
                        <Alert isOpen status="error" showCloseIcon={false}>
                            {errorMsg}
                        </Alert>
                    </div>
                )}

                <div className="form-row">
                    <Button
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isSubmitEnabled || Boolean(submitButtonIsDisabled)}
                    >
                        {cdaEntry.submitButtonLabel}
                    </Button>
                </div>
            </form>
        </div>
    );
}
